import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fiveStarApi } from '../lib/api';
import { Star, ArrowLeft, CheckCircle2, Circle, FileText, MessageSquare, PenSquare, Users as UsersIcon, Save, Send, Lock, Trash2, UserCog, Eye, Shield } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import TruncatedDescription from '../components/TruncatedDescription';
import EmployeeTypeahead from '../components/EmployeeTypeahead';

const STEPS = [
  { key: 'manager_rating',      label: 'Manager Rates',          short: 'Mgr Rate' },
  { key: 'area_manager_review', label: 'Area Manager Reviews',   short: 'AM Review' },
  { key: 'manager_synthesis',   label: 'Manager Synthesizes',    short: 'Synthesis' },
  { key: 'signed_off',          label: 'Final Sign-off',         short: 'Sign-off' },
  { key: 'discussion_pending',  label: 'Employee Discussion',    short: 'Discuss' },
];

const stepIndex = (status) => {
  if (status === 'discussed') return STEPS.length;
  const i = STEPS.findIndex(s => s.key === status);
  return i === -1 ? 0 : i;
};

/**
 * Chevron-shaped rating selector (1-5).
 * Renders 5 slanted parallelogram tiles with a green accent strip top & bottom.
 */
const ChevronRating = ({ value, onChange, readOnly = false, size = 'md', testId }) => {
  const [hover, setHover] = useState(0);
  const active = hover || value || 0;

  // Size variants
  const sizes = {
    sm: { w: 22, h: 30, fs: 11 },
    md: { w: 34, h: 44, fs: 15 },
    lg: { w: 40, h: 52, fs: 17 },
  }[size] || { w: 34, h: 44, fs: 15 };

  return (
    <div className="inline-flex items-center gap-1.5" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => {
        const isSelected = n <= active;
        return (
          <button
            key={n}
            type="button"
            disabled={readOnly}
            onClick={() => !readOnly && onChange(n)}
            onMouseEnter={() => !readOnly && setHover(n)}
            onMouseLeave={() => !readOnly && setHover(0)}
            aria-label={`Rate ${n}`}
            data-testid={testId ? `${testId}-${n}` : undefined}
            className={`relative flex items-center justify-center font-bold transition-all ${
              readOnly ? 'cursor-default' : 'cursor-pointer hover:-translate-y-0.5'
            }`}
            style={{
              width: sizes.w,
              height: sizes.h,
              fontSize: sizes.fs,
              transform: 'skewX(-12deg)',
              background: isSelected ? '#1e7d32' : '#ffffff',
              color: isSelected ? '#ffffff' : '#475569',
              border: '1.5px solid #1e7d32',
              boxShadow: isSelected ? '0 4px 12px rgba(30,125,50,0.25)' : 'none',
            }}
          >
            {/* Counter-skew so the number stays upright */}
            <span style={{ transform: 'skewX(12deg)' }}>{n}</span>
          </button>
        );
      })}
      {value > 0 && (
        <span className="ml-3 text-sm font-semibold text-slate-700">{Number(value).toFixed(1)}</span>
      )}
    </div>
  );
};

// Alias for backward compatibility with existing usages in this file
const StarRating = ChevronRating;

const Stepper = ({ status, onStepClick, canViewHistory }) => {
  const idx = stepIndex(status);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 shadow-sm" data-testid="workflow-stepper">
      <div className="flex items-center justify-between gap-2 overflow-x-auto">
        {STEPS.map((s, i) => {
          const isDone = i < idx;
          const isActive = i === idx;
          // Manager + AM can click any completed step to view it read-only.
          // Employees cannot (they shouldn't see the raw manager comments).
          const clickable = canViewHistory && (isDone || isActive);
          const content = (
            <>
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 border-2 transition ${
                  isDone
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : isActive
                      ? 'bg-fuchsia-500 border-fuchsia-500 text-white shadow-[0_0_0_4px_rgba(213,0,249,0.18)]'
                      : 'bg-white border-slate-300 text-slate-400'
                } ${clickable ? 'group-hover:scale-110 group-hover:shadow-md' : ''}`}
              >
                {isDone ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              <div className="min-w-0 text-left">
                <div className={`text-xs font-semibold whitespace-nowrap ${isActive ? 'text-fuchsia-700' : isDone ? 'text-slate-900' : 'text-slate-400'} ${clickable ? 'group-hover:underline' : ''}`}>
                  {s.label}
                </div>
                <div className="text-[10px] text-slate-400">
                  {isDone ? 'Complete · click to view' : isActive ? (clickable ? 'Active · click to view' : 'Active') : 'Pending'}
                </div>
              </div>
            </>
          );
          return (
            <React.Fragment key={s.key}>
              {clickable ? (
                <button
                  type="button"
                  onClick={() => onStepClick(i)}
                  data-testid={`stepper-step-${s.key}`}
                  className="group flex items-center gap-2 min-w-0 cursor-pointer rounded-lg px-1 -mx-1 py-1 hover:bg-slate-50 transition"
                  title={`View what was entered at: ${s.label}`}
                >
                  {content}
                </button>
              ) : (
                <div className="flex items-center gap-2 min-w-0">{content}</div>
              )}
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 min-w-[24px] ${i < idx ? 'bg-emerald-400' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {canViewHistory && (
        <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-500 flex items-center gap-1.5">
          <Eye className="w-3 h-3" /> Click any completed stage above to view what was entered (read-only).
        </div>
      )}
    </div>
  );
};

/**
 * Read-only modal that shows everything captured at a single workflow stage.
 * Opened from the Stepper when the viewer is the manager or AM.
 */
const StageHistoryModal = ({ open, stepIndex: si, review, categories, onClose, onEditManagerRating, onEditAreaManagerReview, canEditMgrRating, canEditAmReview }) => {
  if (!open || si == null) return null;
  const step = STEPS[si];
  if (!step) return null;

  const catName = (id) => categories.find(c => c.id === id)?.name || id;

  const sectionByKey = () => {
    switch (step.key) {
      case 'manager_rating': {
        const mr = review.manager_rating;
        if (!mr) return <Empty label="The manager hasn't submitted their initial rating yet." />;
        return (
          <>
            <Meta by={review.manager_name} when={mr.submitted_at} />
            <RatingsBlock ratings={mr.ratings} catName={catName} />
            <CommentBlock label="Overall comment from the manager" text={mr.overall_comment} />
            {(mr.strengths?.length || mr.growth_areas?.length) ? (
              <SgBlock strengths={mr.strengths} growth={mr.growth_areas} />
            ) : null}
          </>
        );
      }
      case 'area_manager_review': {
        const am = review.area_manager_review;
        if (review.area_manager_opted_out) {
          return <Empty label={`The Area Manager (${review.area_manager_name || '—'}) opted out of this review.`} />;
        }
        if (!am) {
          if (!review.area_manager_user_id) return <Empty label="No Area Manager was assigned — this step was skipped." />;
          return <Empty label={`Awaiting input from ${review.area_manager_name || 'the Area Manager'}.`} />;
        }
        return (
          <>
            <Meta by={review.area_manager_name} when={am.submitted_at} />
            <RatingsBlock ratings={am.ratings} catName={catName} />
            <CommentBlock label="Overall comment from the Area Manager" text={am.overall_comment} />
            {Object.keys(review.am_pushback_notes || {}).length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-2">Push-back notes</div>
                <div className="space-y-2">
                  {Object.entries(review.am_pushback_notes).map(([cid, n]) => (
                    <div key={cid} className="p-2.5 rounded-md bg-amber-50 border border-amber-200">
                      <div className="text-[11px] font-semibold text-amber-800">{catName(cid)}</div>
                      <div className="text-sm text-slate-800 whitespace-pre-wrap leading-snug">{n.note}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{n.by_name || n.by_email}{n.updated_at && <> · {new Date(n.updated_at).toLocaleString()}</>}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      }
      case 'manager_synthesis': {
        const syn = review.synthesis;
        if (!syn) return <Empty label="The manager hasn't synthesized this review yet." />;
        return (
          <>
            <Meta by={review.manager_name} when={syn.submitted_at} />
            <RatingsBlock ratings={syn.final_ratings} catName={catName} title="Final Ratings" />
            <CommentBlock label="Synthesis summary" text={syn.summary} />
            <SgBlock strengths={syn.strengths} growth={syn.growth_areas} />
            {syn.goals?.length > 0 && (
              <ListBlock label="Goals proposed" items={syn.goals} />
            )}
            {review.final_overall_score != null && (
              <div className="mt-4 pt-4 border-t border-slate-100 text-sm">
                <strong className="text-fuchsia-700">Final overall score:</strong> {review.final_overall_score.toFixed(2)} / 5
              </div>
            )}
          </>
        );
      }
      case 'signed_off': {
        const so = review.sign_off;
        if (!so) return <Empty label="Not yet signed off." />;
        return (
          <>
            <Meta by={so.signed_by || review.manager_name} when={so.signed_at} />
            <div className="text-sm text-slate-700">
              The manager signed off on the synthesis. The review is now queued for the live discussion meeting with the employee.
            </div>
            {so.signature_text && (
              <CommentBlock label="Sign-off statement" text={so.signature_text} />
            )}
          </>
        );
      }
      case 'discussion_pending': {
        const d = review.discussion;
        if (!d) return <Empty label="The discussion hasn't happened yet." />;
        return (
          <>
            <Meta by={review.employee_name} when={d.discussed_at || review.updated_at} />
            <CommentBlock label="Employee comments" text={d.employee_comments} />
            {d.action_items?.length > 0 && <ListBlock label="Action items" items={d.action_items} />}
            {d.goals?.length > 0 && <ListBlock label="Goals" items={d.goals} />}
            {d.follow_up_date && (
              <div className="mt-3 text-sm"><strong>Follow-up date:</strong> {new Date(d.follow_up_date).toLocaleDateString()}</div>
            )}
            {d.acknowledged && (
              <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                <CheckCircle2 className="w-3.5 h-3.5" /> Employee acknowledged the review
              </div>
            )}
          </>
        );
      }
      default:
        return null;
    }
  };

  const stepKey = step.key;
  const showEditMgr = stepKey === 'manager_rating' && canEditMgrRating && !!onEditManagerRating;
  const showEditAm  = stepKey === 'area_manager_review' && canEditAmReview && !!onEditAreaManagerReview;

  return (
    <div className="fixed inset-0 bg-slate-900/55 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="stage-history-modal">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-fuchsia-600 mb-0.5">Stage {si + 1} of {STEPS.length} — {(showEditMgr || showEditAm) ? 'view + edit' : 'read only'}</div>
            <h3 className="text-lg font-bold text-slate-900">{step.label}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" type="button"><span className="text-slate-500 text-xl leading-none">×</span></button>
        </div>
        <div className="space-y-2">{sectionByKey()}</div>
        <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
          {(showEditMgr || showEditAm) ? (
            <>
              <div className="text-[11px] text-slate-500">
                Editing keeps the workflow at its current stage — your changes flow into synthesis.
              </div>
              <button
                type="button"
                data-testid={showEditMgr ? 'stage-history-edit-mgr-btn' : 'stage-history-edit-am-btn'}
                onClick={() => { onClose(); (showEditMgr ? onEditManagerRating : onEditAreaManagerReview)(); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-gradient-to-r from-fuchsia-600 to-cyan-600 text-white hover:opacity-90 shadow-sm"
              >
                ✏️ Edit this {showEditMgr ? 'rating' : 'review'}
              </button>
            </>
          ) : (
            <div className="text-[11px] text-slate-500">
              Viewing as read-only. To make changes, return to the active step on the main review page.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Small composable blocks used by StageHistoryModal
const Empty = ({ label }) => <div className="text-sm text-slate-500 italic py-3">{label}</div>;
const Meta = ({ by, when }) => (
  <div className="text-[11px] text-slate-500 mb-3">by {by || '—'}{when && <> · {new Date(when).toLocaleString()}</>}</div>
);
const RatingsBlock = ({ ratings, catName, title = 'Ratings' }) => {
  if (!Array.isArray(ratings) || ratings.length === 0) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-2">{title}</div>
      <div className="space-y-2">
        {ratings.map(r => (
          <div key={r.category_id} className="text-sm">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-800">{r.category_name || catName(r.category_id)}</span>
              <StarRating value={r.rating || 0} readOnly size="sm" />
            </div>
            {r.comment && <div className="text-sm text-slate-700 whitespace-pre-wrap leading-snug mt-0.5">{r.comment}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};
const CommentBlock = ({ label, text }) => {
  if (!text || !text.trim()) return null;
  return (
    <div className="mt-3">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">{label}</div>
      <div className="text-sm text-slate-700 whitespace-pre-wrap leading-snug p-3 rounded-md bg-slate-50 border border-slate-100">{text}</div>
    </div>
  );
};
const SgBlock = ({ strengths, growth }) => {
  if (!(strengths?.length || growth?.length)) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
      {strengths?.length > 0 && <ListBlock label="Strengths" items={strengths} tone="emerald" />}
      {growth?.length > 0 && <ListBlock label="Growth areas" items={growth} tone="cyan" />}
    </div>
  );
};
const ListBlock = ({ label, items, tone = 'slate' }) => {
  const toneMap = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  }[tone];
  return (
    <div className={`p-3 rounded-md border ${toneMap}`}>
      <div className="text-[11px] font-bold uppercase tracking-wider mb-1.5">{label}</div>
      <ul className="space-y-1 text-sm">
        {items.filter(Boolean).map((it, i) => (
          <li key={i} className="leading-snug">• {it}</li>
        ))}
      </ul>
    </div>
  );
};

const Card = ({ title, icon: Icon, children, testId }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm mb-5" data-testid={testId}>
    {title && (
      <div className="flex items-center gap-2 text-base font-bold text-slate-900 mb-4">
        {Icon && <Icon className="w-4 h-4 text-fuchsia-600" />}
        {title}
      </div>
    )}
    {children}
  </div>
);

/**
 * Editable bullet-list of free-text strings. Used for Goals & Action Items at every step.
 */
const StringListEditor = ({ label, items, onChange, placeholder, testId, readOnly = false }) => {
  const safeItems = Array.isArray(items) ? items : [];
  const setAt = (i, v) => {
    const next = [...safeItems];
    next[i] = v;
    onChange(next);
  };
  const removeAt = (i) => onChange(safeItems.filter((_, j) => j !== i));
  const add = () => onChange([...safeItems, '']);
  return (
    <div className="mb-4" data-testid={testId}>
      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{label}</label>
      <div className="space-y-2">
        {safeItems.map((s, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="text-slate-300 text-sm pt-2.5">•</span>
            <textarea
              value={s}
              disabled={readOnly}
              onChange={(e) => setAt(i, e.target.value)}
              placeholder={placeholder}
              rows={1}
              data-testid={`${testId}-item-${i}`}
              className="flex-1 text-sm px-3 py-2 rounded-md border border-slate-200 bg-slate-50 focus:outline-none focus:border-fuchsia-400 focus:bg-white focus:ring-1 focus:ring-fuchsia-200 resize-y disabled:bg-slate-100 disabled:text-slate-600"
            />
            {!readOnly && (
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="px-2 py-1 text-xs text-rose-500 hover:bg-rose-50 rounded"
                data-testid={`${testId}-remove-${i}`}
              >Remove</button>
            )}
          </div>
        ))}
        {safeItems.length === 0 && readOnly && (
          <div className="text-xs text-slate-400 italic">None recorded.</div>
        )}
        {!readOnly && (
          <button
            type="button"
            onClick={add}
            data-testid={`${testId}-add`}
            className="text-xs font-semibold text-fuchsia-600 hover:text-fuchsia-700"
          >+ Add another</button>
        )}
      </div>
    </div>
  );
};

const RatingForm = ({
  categories,
  initialRatings,
  initialComment,
  initialStrengths,
  initialGrowthAreas,
  onSave,
  submitLabel,
  allowEmptyRatings = false,
  savingLabel = 'Saving…',
  commentsRequired = false,
  requireOverall = false,
  withStrengthsAndGrowth = false,
  lockedCategories = [],
  autosave = null,        // optional async fn called with the partial body whenever the user edits
}) => {
  const [ratings, setRatings] = useState(() => {
    const map = {};
    (initialRatings || []).forEach(r => { map[r.category_id] = { rating: r.rating, comment: r.comment }; });
    return map;
  });
  const [overallComment, setOverallComment] = useState(initialComment || '');
  const [strengthsText, setStrengthsText] = useState((initialStrengths || []).join('\n'));
  const [growthText, setGrowthText] = useState((initialGrowthAreas || []).join('\n'));
  const [saving, setSaving] = useState(false);
  // Autosave status: 'idle' | 'unsaved' | 'saving' | 'saved' | 'error'
  const [autoStatus, setAutoStatus] = useState('idle');
  const autoTimerRef = React.useRef(null);
  const lastAutosaveRef = React.useRef(null);

  // Debounced autosave whenever the user edits anything
  React.useEffect(() => {
    if (!autosave) return undefined;
    setAutoStatus('unsaved');
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    autoTimerRef.current = setTimeout(async () => {
      const payload = categories.map(c => ({
        category_id: c.id,
        category_name: c.name,
        rating: ratings[c.id]?.rating ?? null,
        comment: ratings[c.id]?.comment || '',
      }));
      const body = { ratings: payload, overall_comment: overallComment };
      if (withStrengthsAndGrowth) {
        body.strengths = strengthsText.split('\n').map(s => s.trim()).filter(Boolean);
        body.growth_areas = growthText.split('\n').map(s => s.trim()).filter(Boolean);
      }
      const snap = JSON.stringify(body);
      if (snap === lastAutosaveRef.current) return; // nothing new
      setAutoStatus('saving');
      try {
        await autosave(body);
        lastAutosaveRef.current = snap;
        setAutoStatus('saved');
      } catch (_e) {
        setAutoStatus('error');
      }
    }, 2000);
    return () => {
      if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratings, overallComment, strengthsText, growthText]);

  const updateRating = (catId, field, value) => {
    setRatings(prev => ({ ...prev, [catId]: { ...(prev[catId] || {}), [field]: value } }));
  };

  const handleSubmit = async () => {
    const payload = categories.map(c => ({
      category_id: c.id,
      category_name: c.name,
      rating: ratings[c.id]?.rating ?? null,
      comment: ratings[c.id]?.comment || '',
    }));
    if (!allowEmptyRatings) {
      const missing = payload.filter(p => p.rating == null);
      if (missing.length > 0) {
        toast.error(`Please rate all ${categories.length} categories`);
        return;
      }
    }
    if (commentsRequired) {
      const blankCats = payload.filter(p => !(p.comment || '').trim()).map(p => p.category_name);
      if (blankCats.length > 0) {
        toast.error(`Comments are required for: ${blankCats.join(', ')}`);
        return;
      }
    }
    if (requireOverall && !overallComment.trim()) {
      toast.error('Overall comments are required.');
      return;
    }
    setSaving(true);
    try {
      const body = { ratings: payload, overall_comment: overallComment };
      if (withStrengthsAndGrowth) {
        body.strengths = strengthsText.split('\n').map(s => s.trim()).filter(Boolean);
        body.growth_areas = growthText.split('\n').map(s => s.trim()).filter(Boolean);
      }
      await onSave(body);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {categories.length === 0 && (
        <div className="my-4 px-4 py-3 rounded-md border border-amber-300 bg-amber-50 text-xs text-amber-800" data-testid="rating-categories-missing">
          <strong>No active 5-Star categories were loaded.</strong> Refresh the page; if this persists, ask a Super Admin to confirm categories are seeded.
        </div>
      )}
      {categories.map(c => {
        const isLocked = (lockedCategories || []).includes(c.id);
        return (
        <div key={c.id} className="py-4 border-b border-slate-100 last:border-b-0" data-testid={`category-${c.id}`}>
          <div className="flex justify-between items-start gap-4 mb-1.5">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                {c.name}
                {isLocked && (
                  <span title="Final rating set by Area Manager — cannot be changed" className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                    <Lock className="w-2.5 h-2.5" /> AM Final
                  </span>
                )}
              </div>
              <TruncatedDescription title={c.name} text={c.description} className="mt-0.5 max-w-xl" />
            </div>
            <StarRating
              value={ratings[c.id]?.rating || 0}
              onChange={(v) => !isLocked && updateRating(c.id, 'rating', v)}
              readOnly={isLocked}
              testId={`rating-${c.id}`}
            />
          </div>
          <textarea
            placeholder={isLocked ? 'Locked — AM has set the final rating for this category' : 'Add comments…'}
            value={ratings[c.id]?.comment || ''}
            onChange={(e) => !isLocked && updateRating(c.id, 'comment', e.target.value)}
            readOnly={isLocked}
            data-testid={`comment-${c.id}`}
            className={`mt-2 w-full text-sm px-3 py-2 rounded-md border border-slate-200 bg-slate-50 focus:outline-none focus:border-fuchsia-400 focus:bg-white focus:ring-1 focus:ring-fuchsia-200 resize-y min-h-[60px] ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
          />
        </div>
        );
      })}
      <div className="mt-5">
        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
          Overall comments {requireOverall && <span className="text-rose-500">*</span>}
        </label>
        <textarea
          value={overallComment}
          onChange={(e) => setOverallComment(e.target.value)}
          rows={3}
          data-testid="overall-comment"
          className="w-full text-sm px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-fuchsia-400 focus:ring-1 focus:ring-fuchsia-200 resize-y"
          placeholder="Big-picture thoughts, themes, context…"
        />
      </div>
      {withStrengthsAndGrowth && (
        <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1.5">
              Strengths <span className="text-slate-400 font-normal normal-case tracking-normal">(one per line)</span>
            </label>
            <textarea
              value={strengthsText}
              onChange={(e) => setStrengthsText(e.target.value)}
              rows={4}
              data-testid="rating-strengths"
              placeholder={'Exceptional teammate, mentors new hires\nPatient relationships consistently 5/5'}
              className="w-full text-sm px-3 py-2 rounded-md border border-emerald-200 bg-emerald-50/40 focus:outline-none focus:bg-white focus:border-emerald-400 resize-y"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-amber-700 mb-1.5">
              Growth Areas <span className="text-slate-400 font-normal normal-case tracking-normal">(one per line)</span>
            </label>
            <textarea
              value={growthText}
              onChange={(e) => setGrowthText(e.target.value)}
              rows={4}
              data-testid="rating-growth"
              placeholder={'Documentation timeliness — target 90% same-day\nContinue advanced CEU coursework'}
              className="w-full text-sm px-3 py-2 rounded-md border border-amber-200 bg-amber-50/40 focus:outline-none focus:bg-white focus:border-amber-400 resize-y"
            />
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mt-5 gap-3">
        {autosave ? (
          <div className="text-xs text-slate-500 inline-flex items-center gap-1.5" data-testid="autosave-status">
            {autoStatus === 'idle' && <span className="text-slate-400">Auto-save ready</span>}
            {autoStatus === 'unsaved' && <span className="text-amber-600">● Unsaved changes</span>}
            {autoStatus === 'saving' && <span className="text-cyan-600">● Saving…</span>}
            {autoStatus === 'saved' && <span className="text-emerald-600">● Saved as draft</span>}
            {autoStatus === 'error' && <span className="text-rose-600">● Save failed — retrying on next edit</span>}
          </div>
        ) : <span />}
        <button
          onClick={handleSubmit}
          disabled={saving}
          data-testid="rating-submit-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-fuchsia-500 to-fuchsia-700 text-white shadow hover:shadow-lg disabled:opacity-60"
        >
          <Send className="w-4 h-4" /> {saving ? savingLabel : submitLabel}
        </button>
      </div>
    </div>
  );
};

const SynthesisForm = ({ categories, review, onSave }) => {
  // Pre-fill final ratings from manager_rating if present
  const initialFinal = useMemo(() => {
    const map = {};
    (review.manager_rating?.ratings || []).forEach(r => { map[r.category_id] = { rating: r.rating, comment: r.comment }; });
    return map;
  }, [review]);

  // Pre-fill goals + action items from manager_rating (item #3 — carry forward) — synthesis is the first place goals/actions appear
  const [final, setFinal] = useState(initialFinal);
  const [strengths, setStrengths] = useState(((review.manager_rating?.strengths) || []).join('\n'));
  const [growth, setGrowth] = useState(((review.manager_rating?.growth_areas) || []).join('\n'));
  const [summary, setSummary] = useState('');
  const [summaryIsAi, setSummaryIsAi] = useState(false);
  const [aiUsesRemaining, setAiUsesRemaining] = useState(Math.max(0, 3 - (review.ai_summary_count || 0)));
  const [generatingAi, setGeneratingAi] = useState(false);
  const [goals, setGoals] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [saving, setSaving] = useState(false);
  // Track which categories currently hold an AI-merged comment so we can show
  // a small badge until the manager edits the comment.
  const [aiMergedCatIds, setAiMergedCatIds] = useState(new Set());

  const handleGenerateAiSummary = async () => {
    if (aiUsesRemaining <= 0) {
      toast.error('AI summary has been generated the maximum number of times for this review.');
      return;
    }
    if (summary.trim() && !summaryIsAi) {
      // eslint-disable-next-line no-restricted-globals
      if (!confirm('Replace your current summary AND per-category comments with an AI-generated draft? You can edit them afterwards.')) return;
    }
    setGeneratingAi(true);
    try {
      const res = await fiveStarApi.generateAiSummary(review.id);
      setSummary(res.summary || '');
      setSummaryIsAi(true);
      setAiUsesRemaining(res.uses_remaining ?? 0);
      // Also apply AI-merged per-category comments — these blend the manager's
      // and the area manager's per-category notes into a single neutral comment.
      const merged = res.merged_comments || {};
      const mergedCatIds = new Set();
      if (merged && typeof merged === 'object') {
        setFinal(prev => {
          const next = { ...prev };
          Object.entries(merged).forEach(([catId, mergedComment]) => {
            if (!mergedComment) return;
            mergedCatIds.add(catId);
            next[catId] = { ...(next[catId] || {}), comment: mergedComment };
          });
          return next;
        });
      }
      setAiMergedCatIds(mergedCatIds);
      const mergedCount = mergedCatIds.size;
      toast.success(
        `AI draft ready — summary${mergedCount ? ` + ${mergedCount} merged comment${mergedCount === 1 ? '' : 's'}` : ''}. Edit as you like.` +
        (res.uses_remaining > 0 ? ` ${res.uses_remaining} regen${res.uses_remaining === 1 ? '' : 's'} left.` : ' No more regenerations available.')
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI generation failed');
    } finally {
      setGeneratingAi(false);
    }
  };

  const updateFinal = (catId, field, value) => {
    setFinal(prev => ({ ...prev, [catId]: { ...(prev[catId] || {}), [field]: value } }));
    if (field === 'comment') {
      // Manager edited the AI-merged comment — clear the badge for that category.
      setAiMergedCatIds(prev => {
        if (!prev.has(catId)) return prev;
        const next = new Set(prev);
        next.delete(catId);
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    const final_ratings = categories.map(c => ({
      category_id: c.id,
      category_name: c.name,
      rating: final[c.id]?.rating ?? null,
      comment: final[c.id]?.comment || '',
    }));
    if (final_ratings.some(r => r.rating == null)) {
      toast.error('All categories need a final rating');
      return;
    }
    // Item #1 — per-category + summary required on synthesis too
    const blank = final_ratings.filter(r => !(r.comment || '').trim()).map(r => r.category_name);
    if (blank.length > 0) {
      toast.error(`Comments required for: ${blank.join(', ')}`);
      return;
    }
    if (!summary.trim()) {
      toast.error('Synthesis summary is required.');
      return;
    }
    const payload = {
      final_ratings,
      strengths: strengths.split('\n').map(s => s.trim()).filter(Boolean),
      growth_areas: growth.split('\n').map(s => s.trim()).filter(Boolean),
      summary,
      goals: goals.filter(s => s.trim()),
      action_items: actionItems.filter(s => s.trim()),
    };
    setSaving(true);
    try { await onSave(payload); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="text-xs text-slate-500 mb-4">
        Use the manager &amp; area manager inputs to set the final score per category. Then capture key strengths, growth areas, and a summary.
      </div>
      {categories.map(c => {
        const mgr = review.manager_rating?.ratings?.find(r => r.category_id === c.id);
        const am = review.area_manager_review?.ratings?.find(r => r.category_id === c.id);
        return (
          <div key={c.id} className="py-4 border-b border-slate-100 last:border-b-0">
            <div className="flex justify-between items-start gap-4 mb-2">
              <div>
                <div className="text-sm font-semibold text-slate-900">{c.name}</div>
                <div className="text-[11px] text-slate-500 mt-1 space-x-3">
                  <span>Manager: <strong className="text-cyan-600">{mgr?.rating?.toFixed(1) || '—'}</strong></span>
                  <span>Area Mgr: <strong className="text-fuchsia-600">{am?.rating ? am.rating.toFixed(1) : '—'}</strong></span>
                </div>
              </div>
              <StarRating
                value={final[c.id]?.rating || 0}
                onChange={(v) => updateFinal(c.id, 'rating', v)}
                testId={`final-rating-${c.id}`}
              />
            </div>
            <textarea
              placeholder="Final synthesis comment for this category…"
              value={final[c.id]?.comment || ''}
              onChange={(e) => updateFinal(c.id, 'comment', e.target.value)}
              data-testid={`final-comment-${c.id}`}
              className="mt-2 w-full text-sm px-3 py-2 rounded-md border border-slate-200 bg-slate-50 focus:outline-none focus:border-fuchsia-400 focus:bg-white resize-y min-h-[56px]"
            />
            {aiMergedCatIds.has(c.id) && (
              <div data-testid={`ai-merged-badge-${c.id}`} className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gradient-to-r from-fuchsia-100 to-cyan-100 text-fuchsia-700 border border-fuchsia-200">
                ✨ AI-merged from Manager + Area Manager — edit as you like
              </div>
            )}
          </div>
        );
      })}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1.5">Strengths (one per line)</label>
          <textarea rows={5} value={strengths} onChange={e => setStrengths(e.target.value)} data-testid="synthesis-strengths"
            className="w-full text-sm px-3 py-2 rounded-md border border-emerald-200 bg-emerald-50/40 focus:outline-none focus:bg-white focus:border-emerald-400 resize-y"
            placeholder="Exceptional teammate, mentors new hires&#10;Patient relationships consistently 5/5" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-amber-700 mb-1.5">Growth Areas (one per line)</label>
          <textarea rows={5} value={growth} onChange={e => setGrowth(e.target.value)} data-testid="synthesis-growth"
            className="w-full text-sm px-3 py-2 rounded-md border border-amber-200 bg-amber-50/40 focus:outline-none focus:bg-white focus:border-amber-400 resize-y"
            placeholder="Documentation timeliness — target 90% same-day&#10;Continue advanced CEU coursework" />
        </div>
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Summary <span className="text-rose-500">*</span>
            {summaryIsAi && (
              <span data-testid="ai-summary-badge" className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold normal-case tracking-normal px-1.5 py-0.5 rounded bg-gradient-to-r from-fuchsia-100 to-cyan-100 text-fuchsia-700 border border-fuchsia-200">
                ✨ AI-generated draft — review &amp; edit
              </span>
            )}
          </label>
          <button
            type="button"
            onClick={handleGenerateAiSummary}
            disabled={generatingAi || aiUsesRemaining <= 0}
            data-testid="ai-summarize-btn"
            title={aiUsesRemaining <= 0 ? 'Maximum regenerations reached for this review.' : `${aiUsesRemaining} generation${aiUsesRemaining === 1 ? '' : 's'} remaining.`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-fuchsia-200 bg-gradient-to-r from-fuchsia-50 to-cyan-50 text-fuchsia-700 hover:from-fuchsia-100 hover:to-cyan-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {generatingAi ? (
              <>
                <span className="inline-block w-3 h-3 border-2 border-fuchsia-400 border-t-transparent rounded-full animate-spin" />
                Generating…
              </>
            ) : (
              <>✨ {summary.trim() ? 'Regenerate' : 'Generate'} AI Summary {aiUsesRemaining > 0 && <span className="text-[10px] opacity-70">({aiUsesRemaining} left)</span>}</>
            )}
          </button>
        </div>
        <textarea rows={summaryIsAi ? 6 : 3} value={summary} onChange={e => { setSummary(e.target.value); setSummaryIsAi(false); }} data-testid="synthesis-summary"
          className="w-full text-sm px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-fuchsia-400 resize-y"
          placeholder="Overall narrative the employee will see… (or click ✨ AI Summary to draft one)" />
      </div>
      {/* Item #3 — Goals + Action Items are introduced at synthesis (the discussion step lets you refine them further) */}
      <div className="mt-5 pt-5 border-t border-slate-100">
        <StringListEditor
          label="Goals"
          items={goals}
          onChange={setGoals}
          placeholder="e.g. 90% same-day documentation by next cycle"
          testId="synthesis-goals"
        />
        <StringListEditor
          label="Action Items"
          items={actionItems}
          onChange={setActionItems}
          placeholder="e.g. Complete advanced geriatrics CEU by Aug 1"
          testId="synthesis-action-items"
        />
      </div>
      <div className="flex justify-end mt-5">
        <button onClick={handleSubmit} disabled={saving} data-testid="synthesis-submit-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-fuchsia-500 to-fuchsia-700 text-white shadow hover:shadow-lg disabled:opacity-60">
          <Send className="w-4 h-4" /> {saving ? 'Submitting…' : 'Submit Synthesis'}
        </button>
      </div>
    </div>
  );
};

const ReadOnlyRatings = ({ title, by, when, ratings, comment, color = 'cyan', hideComments = false }) => {
  if (!ratings) return null;
  const colorMap = {
    cyan: 'border-cyan-200 bg-cyan-50/40',
    magenta: 'border-fuchsia-200 bg-fuchsia-50/40',
    emerald: 'border-emerald-200 bg-emerald-50/40',
  }[color];
  return (
    <div className={`rounded-xl border ${colorMap} p-5 mb-4`}>
      <div className="text-xs font-bold uppercase tracking-wide text-slate-700 mb-1">{title}</div>
      <div className="text-[11px] text-slate-500 mb-3">by {by} {when && <>· {new Date(when).toLocaleDateString()}</>}</div>
      <div className="space-y-2">
        {(ratings || []).map(r => (
          <div key={r.category_id} className="flex items-center justify-between gap-3 text-sm">
            <div className="text-slate-700">{r.category_name}</div>
            <div className="flex items-center gap-2">
              <StarRating value={r.rating || 0} readOnly size="sm" />
              {!hideComments && r.comment && <span className="text-xs text-slate-500 italic truncate max-w-[200px]">"{r.comment}"</span>}
            </div>
          </div>
        ))}
      </div>
      {!hideComments && comment && (
        <div className="mt-3 pt-3 border-t border-slate-200/60 text-xs text-slate-600 italic">
          "{comment}"
        </div>
      )}
    </div>
  );
};

/**
 * Per-category threaded push-back note row.
 * - When the viewer IS the AM at status `area_manager_review`, they get an
 *   editor (textarea + Save / Cancel / Delete).
 * - When the viewer is the manager at synthesis, they see the saved note as
 *   read-only context alongside their own draft.
 */
const PushbackNoteRow = ({ reviewId, categoryId, existing, canEdit, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(existing?.note || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const text = draft.trim();
    if (!text) { toast.error('Add some text or use Delete to remove the note'); return; }
    setSaving(true);
    try {
      const res = await fiveStarApi.saveAmPushback(reviewId, categoryId, text);
      toast.success('Note saved');
      setEditing(false);
      onChanged?.(categoryId, res.note);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this push-back note?')) return;
    setSaving(true);
    try {
      await fiveStarApi.deleteAmPushback(reviewId, categoryId);
      toast.success('Note removed');
      setEditing(false);
      setDraft('');
      onChanged?.(categoryId, null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  // Read-only display when there's a saved note and we're not editing
  if (existing && !editing) {
    return (
      <div className="mt-2 p-3 rounded-md bg-amber-50 border border-amber-200" data-testid={`pushback-display-${categoryId}`}>
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">
            <MessageSquare className="w-3 h-3" /> Area Manager push-back
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={() => { setDraft(existing.note); setEditing(true); }}
              data-testid={`pushback-edit-${categoryId}`}
              className="text-[10px] font-semibold text-amber-700 hover:text-amber-900"
            >
              Edit
            </button>
          )}
        </div>
        <div className="text-sm text-slate-800 whitespace-pre-wrap leading-snug">{existing.note}</div>
        <div className="text-[10px] text-slate-500 mt-1">
          {existing.by_name || existing.by_email}{existing.updated_at && <> · {new Date(existing.updated_at).toLocaleString()}</>}
        </div>
      </div>
    );
  }

  // Editor — AM only
  if (canEdit && editing) {
    return (
      <div className="mt-2 p-3 rounded-md bg-white border border-amber-300" data-testid={`pushback-editor-${categoryId}`}>
        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1.5 flex items-center gap-1.5">
          <MessageSquare className="w-3 h-3" /> Push back on this category
        </div>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          autoFocus
          placeholder="Add specific evidence, examples, or a question for the manager…"
          data-testid={`pushback-textarea-${categoryId}`}
          className="w-full text-sm rounded-md border border-slate-300 p-2 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200"
        />
        <div className="flex items-center justify-end gap-2 mt-2">
          {existing && (
            <button
              type="button"
              onClick={remove}
              disabled={saving}
              data-testid={`pushback-delete-${categoryId}`}
              className="px-2.5 py-1 text-[11px] font-semibold rounded border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
            >
              Delete
            </button>
          )}
          <button
            type="button"
            onClick={() => { setEditing(false); setDraft(existing?.note || ''); }}
            disabled={saving}
            className="px-2.5 py-1 text-[11px] font-semibold rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !draft.trim()}
            data-testid={`pushback-save-${categoryId}`}
            className="px-3 py-1 text-[11px] font-semibold rounded bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save note'}
          </button>
        </div>
      </div>
    );
  }

  // No note yet, AM can add one
  if (canEdit) {
    return (
      <button
        type="button"
        onClick={() => { setDraft(''); setEditing(true); }}
        data-testid={`pushback-add-${categoryId}`}
        className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:text-amber-900"
      >
        <MessageSquare className="w-3 h-3" /> Push back / add note
      </button>
    );
  }

  return null;
};

/**
 * Expanded, fully-readable panel showing every per-category comment the manager
 * left, plus their overall comment. Surfaced to the Area Manager during their
 * review step and to the manager during synthesis. NEVER shown to the employee.
 *
 * Use this in addition to (not instead of) ReadOnlyRatings — the compact card
 * gives a quick "scoring at a glance" overview, this panel gives the readable
 * context the AM needs to actually push back or add insight.
 */
const ManagerCommentsPanel = ({ reviewId, ratings, overallComment, by, when, pushbackNotes, canEditPushback, onPushbackChanged }) => {
  const items = (ratings || []).filter(r => (r.comment || '').trim().length > 0);
  if (items.length === 0 && !(overallComment || '').trim()) return null;
  return (
    <div className="rounded-xl border border-cyan-300 bg-white p-5 mb-4" data-testid="manager-comments-panel">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-xs font-bold uppercase tracking-wide text-cyan-800">Manager's full comments</div>
        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700 border border-cyan-200">Reviewer view</span>
      </div>
      <div className="text-[11px] text-slate-500 mb-4">by {by} {when && <>· {new Date(when).toLocaleDateString()}</>} — not visible to the employee.</div>
      {items.length > 0 && (
        <div className="space-y-3">
          {items.map(r => (
            <div key={r.category_id} className="border-l-4 border-cyan-200 pl-3">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-sm font-semibold text-slate-800">{r.category_name}</span>
                <StarRating value={r.rating || 0} readOnly size="sm" />
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap leading-snug">{r.comment}</div>
              <PushbackNoteRow
                reviewId={reviewId}
                categoryId={r.category_id}
                existing={pushbackNotes?.[r.category_id]}
                canEdit={canEditPushback}
                onChanged={onPushbackChanged}
              />
            </div>
          ))}
        </div>
      )}
      {(overallComment || '').trim() && (
        <div className="mt-4 pt-4 border-t border-cyan-100">
          <div className="text-[11px] font-bold uppercase tracking-wider text-cyan-800 mb-1">Overall manager comment</div>
          <div className="text-sm text-slate-700 whitespace-pre-wrap leading-snug">{overallComment}</div>
        </div>
      )}
    </div>
  );
};

const SidebarInfo = ({ review }) => (
  <div className="space-y-4">
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Review Summary</div>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">Employee</span><span className="font-semibold">{review.employee_name}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Manager</span><span className="font-semibold">{review.manager_name}</span></div>
        {review.area_manager_name && (
          <div className="flex justify-between"><span className="text-slate-500">Area Manager</span><span className="font-semibold">{review.area_manager_name}</span></div>
        )}
        <div className="flex justify-between"><span className="text-slate-500">Cycle</span><span className="font-semibold text-fuchsia-600">{review.cycle_name}</span></div>
        {review.final_overall_score != null && (
          <div className="flex justify-between"><span className="text-slate-500">Final Score</span>
            <span className="font-bold text-amber-600 flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />{review.final_overall_score.toFixed(1)}</span>
          </div>
        )}
      </div>
    </div>
  </div>
);

const DiscussionForm = ({ onSave, isManager, isEmployee, review }) => {
  const [discussionNotes, setDiscussionNotes] = useState('');
  // Pre-fill goals & action items from synthesis (item #3 — modifiable in discussion)
  const [goals, setGoals] = useState(
    review?.synthesis?.goals || review?.manager_rating?.goals || []
  );
  const [actionItems, setActionItems] = useState(
    review?.synthesis?.action_items || review?.manager_rating?.action_items || []
  );
  const [employeeComments, setEmployeeComments] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!acknowledged) {
      toast.error('Confirm the discussion took place before archiving');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        employee_acknowledged: acknowledged,
        employee_comments: employeeComments,
        discussion_notes: discussionNotes,
        goals: goals.filter(s => s.trim()),
        action_items: actionItems.filter(s => s.trim()),
        follow_up_date: followUpDate || null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="discussion-form">
      <div className="space-y-5">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
            Discussion Notes
          </label>
          <textarea
            value={discussionNotes}
            onChange={(e) => setDiscussionNotes(e.target.value)}
            rows={4}
            data-testid="discussion-notes"
            placeholder="What did you talk about? Themes that came up, employee's reaction, context worth remembering…"
            className="w-full text-sm px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-fuchsia-400 focus:ring-1 focus:ring-fuchsia-200 resize-y"
          />
        </div>

        {/* Item #3 — Goals & Action Items modifiable in discussion */}
        <StringListEditor
          label="Agreed Goals (pre-filled from synthesis — edit as discussed)"
          items={goals}
          onChange={setGoals}
          placeholder="90% same-day documentation by next cycle"
          testId="discussion-goals"
        />
        <StringListEditor
          label="Agreed Action Items (pre-filled from synthesis — edit as discussed)"
          items={actionItems}
          onChange={setActionItems}
          placeholder="Complete advanced geriatrics CEU course by Aug 1"
          testId="discussion-action-items"
        />

        {isEmployee && (
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Your Comments (Employee)
            </label>
            <textarea
              value={employeeComments}
              onChange={(e) => setEmployeeComments(e.target.value)}
              rows={3}
              data-testid="discussion-employee-comments"
              placeholder="Your reflection on the review and conversation…"
              className="w-full text-sm px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-fuchsia-400 resize-y"
            />
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
            Follow-up Check-in <span className="text-slate-400 font-normal normal-case tracking-normal">(optional)</span>
          </label>
          <input
            type="date"
            value={followUpDate}
            onChange={(e) => setFollowUpDate(e.target.value)}
            data-testid="discussion-followup-date"
            className="w-full md:w-auto text-sm px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-fuchsia-400"
          />
        </div>

        <label className="flex items-start gap-2.5 p-3 rounded-lg bg-slate-50 cursor-pointer hover:bg-slate-100 transition">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            data-testid="discussion-acknowledge"
            className="mt-0.5 w-4 h-4 accent-fuchsia-600"
          />
          <span className="text-sm text-slate-700">
            <strong className="text-slate-900">
              {isManager ? 'I confirm this discussion took place.' : 'I acknowledge that we discussed this review.'}
            </strong>
            <br />
            <span className="text-xs text-slate-500">
              Archiving this review will preserve everything captured here for trend tracking.
            </span>
          </span>
        </label>

        <div className="flex justify-end pt-1">
          <button
            onClick={handleSubmit}
            disabled={saving || !acknowledged}
            data-testid="discuss-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-fuchsia-500 to-fuchsia-700 text-white shadow hover:shadow-lg disabled:opacity-60"
          >
            <CheckCircle2 className="w-4 h-4" /> {saving ? 'Archiving…' : 'Complete & Archive Review'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DiscussionSummary = ({ discussion }) => {
  if (!discussion) {
    return (
      <Card title="Review Complete">
        <div className="text-sm text-slate-600">Discussion held. Review archived for trend tracking.</div>
      </Card>
    );
  }
  return (
    <Card title="Review Complete — Discussion Summary" icon={UsersIcon} testId="discussion-summary">
      <div className="text-xs text-slate-500 mb-4">
        Discussion held on {discussion.discussed_at ? new Date(discussion.discussed_at).toLocaleDateString() : '—'}
        {discussion.follow_up_date && <> · Follow-up scheduled for {new Date(discussion.follow_up_date).toLocaleDateString()}</>}
      </div>
      {discussion.discussion_notes && (
        <div className="mb-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Discussion Notes</div>
          <div className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-lg p-3 border border-slate-200">
            {discussion.discussion_notes}
          </div>
        </div>
      )}
      {Array.isArray(discussion.goals) && discussion.goals.length > 0 && (
        <div className="mb-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-1.5">Agreed Goals &amp; Action Items</div>
          <ul className="space-y-1.5 text-sm">
            {discussion.goals.map((g, i) => (
              <li key={i} className="flex items-start gap-2 bg-emerald-50/40 border border-emerald-200 rounded-lg px-3 py-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-800">{g}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {discussion.employee_comments && (
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Employee Comments</div>
          <div className="text-sm text-slate-700 whitespace-pre-wrap italic bg-slate-50 rounded-lg p-3 border border-slate-200">
            "{discussion.employee_comments}"
          </div>
        </div>
      )}
    </Card>
  );
};

const ResultsView = ({ review, categories, trend, discussion, viewerRole = 'manager', onExportPdf }) => {
  // Build per-category comparison rows: Manager → Area Manager → Final
  const mgrMap = {};
  (review.manager_rating?.ratings || []).forEach(r => { mgrMap[r.category_id] = r; });
  const amMap = {};
  (review.area_manager_review?.ratings || []).forEach(r => { amMap[r.category_id] = r; });
  const finalMap = {};
  (review.synthesis?.final_ratings || []).forEach(r => { finalMap[r.category_id] = r; });

  // Use ordered categories (active list); fall back to whatever's in finalMap
  const rows = categories.length > 0
    ? categories.map(c => ({
        id: c.id, name: c.name,
        mgr: mgrMap[c.id]?.rating,
        am: amMap[c.id]?.rating,
        final: finalMap[c.id]?.rating,
      }))
    : Object.keys(finalMap).map(cid => ({
        id: cid, name: finalMap[cid].category_name,
        mgr: mgrMap[cid]?.rating, am: amMap[cid]?.rating, final: finalMap[cid].rating,
      }));

  const finalScore = review.final_overall_score ?? 0;
  const scorePct = Math.max(0, Math.min(100, (finalScore / 5) * 100));

  // Trend delta vs previous cycle
  const trendArr = trend?.trend || [];
  const thisIdx = trendArr.findIndex(t => t.id === review.id);
  const prev = thisIdx > 0 ? trendArr[thisIdx - 1] : (thisIdx === -1 && trendArr.length > 0 ? trendArr[trendArr.length - 1] : null);
  const delta = prev ? +(finalScore - prev.final_overall_score).toFixed(1) : null;

  const strengths = review.synthesis?.strengths || [];
  const growth = review.synthesis?.growth_areas || [];

  // Item #4 — employee sees ONLY the final synthesis (no Manager/AM breakdown columns)
  const isEmployeeView = viewerRole === 'employee';

  // Use the synthesis goals/action_items as the canonical "final" copy; discussion may have updated them.
  const finalGoals = discussion?.goals || review.synthesis?.goals || [];
  const finalActions = discussion?.action_items || review.synthesis?.action_items || [];

  return (
    <div data-testid="results-view" className="space-y-5">
      {/* Item #7 — Export to PDF (top-right action bar) */}
      {onExportPdf && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onExportPdf}
            data-testid="export-pdf-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white shadow hover:bg-slate-800"
          >
            <FileText className="w-4 h-4" /> Download PDF
          </button>
        </div>
      )}
      {/* HERO */}
      <div className="bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-2xl p-7 shadow-sm grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-7 items-center">
        {/* Score ring */}
        <div className="relative w-32 h-32 mx-auto md:mx-0" data-testid="score-ring">
          <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
            <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#e2e8f0" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#d500f9" strokeWidth="3"
              strokeDasharray={`${scorePct} 100`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-3xl font-extrabold text-fuchsia-600 tracking-tight leading-none">{finalScore.toFixed(1)}</div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mt-1">out of 5.0</div>
          </div>
        </div>
        <div>
          <div className="text-[22px] font-extrabold tracking-tight text-slate-900">
            {finalScore >= 4.5 ? 'Exceptional Performance' : finalScore >= 4 ? 'Strong Performance' : finalScore >= 3 ? 'Solid Performance' : finalScore >= 2 ? 'Needs Improvement' : 'Performance Concern'}
          </div>
          <div className="text-sm text-slate-500 mt-1 mb-3">
            Composite of Manager{review.area_manager_name ? ' & Area Manager' : ''} evaluations · {rows.length} {rows.length === 1 ? 'category' : 'categories'}
          </div>
          <ChevronRating value={Math.round(finalScore)} readOnly size="md" />
        </div>
        {delta !== null && (
          <div className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold ${delta >= 0 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}>
            {delta >= 0 ? '▲' : '▼'} {delta > 0 ? '+' : ''}{delta} vs previous cycle
          </div>
        )}
      </div>

      {/* COMPARISON TABLE — manager/AM see full breakdown; employee sees only Final (item #4) */}
      <Card title={isEmployeeView ? 'Final Category Scores' : 'Category Breakdown'} icon={FileText} testId="results-comparison">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="comparison-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-500 font-bold border-b border-slate-200">
                <th className="text-left py-2 pr-3 font-bold">Category</th>
                {!isEmployeeView && <th className="text-left py-2 px-3 font-bold">Manager</th>}
                {!isEmployeeView && review.area_manager_name && !review.area_manager_opted_out && (
                  <th className="text-left py-2 px-3 font-bold">Area Manager</th>
                )}
                <th className="text-left py-2 px-3 font-bold">Final Score</th>
                {!isEmployeeView && <th className="text-left py-2 px-3 font-bold">Comment</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const finalRow = finalMap[row.id];
                return (
                  <tr key={row.id} className="border-b last:border-b-0 border-slate-100">
                    <td className="py-3 pr-3 text-sm font-semibold text-slate-900">{row.name}</td>
                    {!isEmployeeView && (
                      <td className="py-3 px-3">
                        {row.mgr != null
                          ? <span className="inline-flex items-center gap-2 text-cyan-700 font-bold">{row.mgr.toFixed(1)} <ChevronRating value={Math.round(row.mgr)} readOnly size="sm" /></span>
                          : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                    )}
                    {!isEmployeeView && review.area_manager_name && !review.area_manager_opted_out && (
                      <td className="py-3 px-3">
                        {row.am != null
                          ? <span className="inline-flex items-center gap-2 text-fuchsia-700 font-bold">{row.am.toFixed(1)} <ChevronRating value={Math.round(row.am)} readOnly size="sm" /></span>
                          : <span className="text-slate-400 text-xs">no override</span>}
                      </td>
                    )}
                    <td className="py-3 px-3">
                      {row.final != null
                        ? <span className="inline-flex items-center gap-2 font-extrabold text-slate-900">{row.final.toFixed(1)} <ChevronRating value={Math.round(row.final)} readOnly size="sm" /></span>
                        : <span className="text-slate-400 text-xs">—</span>}
                    </td>
                    {!isEmployeeView && (
                      <td className="py-3 px-3 text-xs text-slate-600 italic max-w-md">
                        {finalRow?.comment ? `"${finalRow.comment}"` : '—'}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Final summary (always visible to employee) */}
      {review.synthesis?.summary && (
        <Card title="Final Summary" testId="results-summary">
          <div className="text-sm text-slate-700 whitespace-pre-wrap">{review.synthesis.summary}</div>
        </Card>
      )}

      {/* GOALS & ACTION ITEMS (always visible to everyone, item #3) */}
      {(finalGoals.length > 0 || finalActions.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="results-goals-actions">
          <div className="rounded-xl border border-fuchsia-200 bg-fuchsia-50/40 p-5">
            <div className="text-sm font-bold text-fuchsia-700 mb-3">🎯 Goals</div>
            {finalGoals.length === 0 ? (
              <div className="text-sm text-slate-500 italic">None recorded</div>
            ) : (
              <ul className="space-y-1.5 text-sm text-slate-800">
                {finalGoals.map((g, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="text-fuchsia-600 font-bold mt-0.5">•</span><span>{g}</span></li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-xl border border-cyan-200 bg-cyan-50/40 p-5">
            <div className="text-sm font-bold text-cyan-700 mb-3">✓ Action Items</div>
            {finalActions.length === 0 ? (
              <div className="text-sm text-slate-500 italic">None recorded</div>
            ) : (
              <ul className="space-y-1.5 text-sm text-slate-800">
                {finalActions.map((a, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="text-cyan-600 font-bold mt-0.5">•</span><span>{a}</span></li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* STRENGTHS / GROWTH */}
      {(strengths.length > 0 || growth.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="results-swot">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-5">
            <div className="text-sm font-bold text-emerald-700 mb-3">✓ Strengths</div>
            {strengths.length === 0 ? (
              <div className="text-sm text-slate-500 italic">None noted</div>
            ) : (
              <ul className="space-y-1.5 text-sm text-slate-800">
                {strengths.map((s, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="text-emerald-600 font-bold mt-0.5">•</span><span>{s}</span></li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-5">
            <div className="text-sm font-bold text-amber-700 mb-3">★ Growth Areas</div>
            {growth.length === 0 ? (
              <div className="text-sm text-slate-500 italic">None noted</div>
            ) : (
              <ul className="space-y-1.5 text-sm text-slate-800">
                {growth.map((g, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="text-amber-600 font-bold mt-0.5">•</span><span>{g}</span></li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* TREND CHART */}
      {trendArr.length > 0 && (
        <Card title="Improvement Trend" testId="results-trend">
          <div className="text-xs text-slate-500 mb-4">{trendArr.length} {trendArr.length === 1 ? 'completed cycle' : 'completed cycles'}</div>
          <div className="flex items-end gap-2 h-44">
            {trendArr.map((t) => {
              const isThis = t.id === review.id;
              const height = ((t.final_overall_score || 0) / 5) * 100;
              return (
                <div key={t.id} className="flex-1 h-full max-w-[100px] flex flex-col items-center justify-end gap-1.5 min-w-0">
                  <div className="text-[11px] font-bold text-slate-700">{(t.final_overall_score || 0).toFixed(1)}</div>
                  <div
                    className="w-full rounded-t transition-all"
                    style={{
                      height: `${height}%`,
                      minHeight: 6,
                      background: isThis ? 'linear-gradient(180deg, #d500f9, #00b8d4)' : '#cbd5e1',
                    }}
                  />
                  <div className="text-[9px] font-semibold text-slate-500 truncate max-w-full text-center" title={t.cycle_name}>{t.cycle_name}</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* SIGN-OFF + DISCUSSION SUMMARY */}
      <div className="bg-gradient-to-r from-cyan-50/60 to-fuchsia-50/60 border border-fuchsia-200/50 rounded-xl p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: 'linear-gradient(135deg, #00b8d4, #d500f9)' }}>
          <CheckCircle2 className="w-5 h-5" />
        </div>
        <div className="flex-1 text-sm">
          <strong className="text-slate-900">Final Sign-off Complete</strong>
          <div className="text-xs text-slate-500">
            {review.sign_off?.by_name || review.manager_name} · Signed{review.sign_off?.signed_at ? ` ${new Date(review.sign_off.signed_at).toLocaleDateString()}` : ''}
            {discussion?.discussed_at && <> · Discussed {new Date(discussion.discussed_at).toLocaleDateString()}</>}
          </div>
        </div>
      </div>

      <DiscussionSummary discussion={discussion} />
    </div>
  );
};

const FiveStarReviewPage = () => {
  const { id } = useParams();
  const { user, adminRoles = [] } = useAuth();
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  // Reassign-AM modal state (declared at top to honor React's rule-of-hooks)
  const [showReassignAm, setShowReassignAm] = useState(false);
  const [amCandidates, setAmCandidates] = useState([]);
  const [reassignAmId, setReassignAmId] = useState('');
  const [reassigning, setReassigning] = useState(false);
  // Super-admin override modal (change manager / AM / rewind status)
  const [showOverride, setShowOverride] = useState(false);
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [overrideCandidates, setOverrideCandidates] = useState([]);
  const [override, setOverride] = useState({ manager_user_id: '', area_manager_user_id: '', status: '', reason: '' });
  // Stage History modal — managers + AMs can click any completed step to view it.
  const [stageHistoryIdx, setStageHistoryIdx] = useState(null);
  // Pre-synthesis edit toggles (Feb-2026 — managers & AMs can edit until synthesis submit)
  const [editingMgrRating, setEditingMgrRating] = useState(false);
  const [editingAmReview, setEditingAmReview] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [r, cats] = await Promise.all([
        fiveStarApi.getReview(id),
        fiveStarApi.listCategories(false),
      ]);
      setReview(r);
      setCategories(cats || []);
      // Load trend data only once we know the employee, used by the Results view
      if (r?.status === 'discussed' && r?.employee_user_id) {
        try {
          const t = await fiveStarApi.getTrend(r.employee_user_id);
          setTrend(t);
        } catch (_) {
          setTrend({ trend: [] });
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load review');
      navigate('/five-star');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  if (loading || !review) {
    return <div className="px-12 py-10 text-sm text-slate-500">Loading review…</div>;
  }

  const isManager = user?.id === review.manager_user_id;
  const isAreaManager = user?.id === review.area_manager_user_id;
  const isEmployee = user?.id === review.employee_user_id;

  const handleManagerRate = async (payload) => {
    try { await fiveStarApi.submitManagerRating(id, payload); toast.success('Submitted to next step'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const handleAreaManagerReview = async (payload) => {
    try { await fiveStarApi.submitAreaManagerReview(id, payload); toast.success('Area Manager review submitted'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const handleSynthesis = async (payload) => {
    try { await fiveStarApi.submitSynthesis(id, payload); toast.success('Synthesis complete — ready for sign-off'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const handleSignOff = async () => {
    try { await fiveStarApi.submitSignOff(id, { notes: '' }); toast.success('Signed off — schedule discussion with employee'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const handleDiscuss = async (payload) => {
    try { await fiveStarApi.submitDiscussion(id, payload); toast.success('Discussion complete — review archived'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const handleAreaManagerOptOut = async () => {    // eslint-disable-next-line no-restricted-globals
    const notes = prompt('Optional: brief reason for opting out (you can leave blank).', '');
    if (notes === null) return; // user cancelled
    try {
      await fiveStarApi.areaManagerOptOut(id, notes);
      toast.success('Opted out — review will continue without your input.');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to opt out');
    }
  };

  // ── Pre-Synthesis Edits ─────────────────────────────────────────────────
  // Managers can edit THEIR rating until the manager submits final synthesis.
  // Area Managers can edit BOTH the manager rating AND their own AM review
  // until the manager submits synthesis. Status never advances on edit.
  const handleEditManagerRating = async (payload) => {
    try {
      await fiveStarApi.editManagerRating(id, payload);
      toast.success('Manager rating updated');
      setEditingMgrRating(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save changes');
    }
  };
  const handleEditAreaManagerReview = async (payload) => {
    try {
      await fiveStarApi.editAreaManagerReview(id, payload);
      toast.success('Area Manager review updated');
      setEditingAmReview(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save changes');
    }
  };

  const handleExportPdf = () => {
    const url = fiveStarApi.exportPdfUrl(id);
    window.open(url, '_blank');
  };

  const canDelete = adminRoles.includes('super_admin');
  const handleDeleteReview = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`PERMANENTLY delete the ${review.cycle_name} review for ${review.employee_name}? This cannot be undone.`)) return;
    try {
      await fiveStarApi.deleteReview(id);
      toast.success('Review deleted');
      navigate('/five-star');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Delete failed');
    }
  };

  // ── Reassign Area Manager ─────────────────────────────────────────────
  // The initiating manager OR any admin can swap the AM while the review is
  // still in early stages (manager_rating or area_manager_review).
  const isAdmin = (adminRoles || []).some(r => ['super_admin', 'template_admin', 'reports_admin'].includes(r));
  const canReassignAm = (isManager || isAdmin)
    && ['manager_rating', 'area_manager_review'].includes(review.status);

  const openReassign = async () => {
    setShowReassignAm(true);
    setReassignAmId('');
    try {
      const d = await fiveStarApi.listEligibleManagers();
      setAmCandidates((d?.managers || []).filter(m => m.id !== review.employee_user_id && m.id !== review.manager_user_id));
    } catch {
      setAmCandidates([]);
    }
  };

  const submitReassign = async () => {
    if (!reassignAmId) { toast.error('Pick a replacement Area Manager'); return; }
    setReassigning(true);
    try {
      await fiveStarApi.reassignAreaManager(id, reassignAmId);
      toast.success('Area Manager reassigned');
      setShowReassignAm(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Reassign failed');
    } finally {
      setReassigning(false);
    }
  };

  // ── Admin Super-Override (super_admin only) ────────────────────────────
  // Change the initiating Manager, the Area Manager, and/or rewind status.
  // Rewind only — the modal restricts the status dropdown to the current
  // step or earlier. Reason is required.
  const isSuperAdmin = (adminRoles || []).includes('super_admin');

  const openOverride = async () => {
    setOverride({
      manager_user_id: review.manager_user_id || '',
      area_manager_user_id: review.area_manager_user_id || '',
      status: review.status || '',
      reason: '',
    });
    setShowOverride(true);
    try {
      // Reuse the eligible-managers list — same pool used for AM reassignment.
      const d = await fiveStarApi.listEligibleManagers();
      setOverrideCandidates((d?.managers || []).filter(m => m.id !== review.employee_user_id));
    } catch {
      setOverrideCandidates([]);
    }
  };

  const OVERRIDE_STATUS_ORDER = ['manager_rating', 'area_manager_review', 'manager_synthesis', 'signed_off', 'discussion_pending', 'discussed', 'expired'];
  const OVERRIDE_STATUS_LABEL = {
    manager_rating: '1 · Manager Rating',
    area_manager_review: '2 · Area Manager Review',
    manager_synthesis: '3 · Manager Synthesis',
    signed_off: '4 · Signed Off (awaiting discussion)',
    discussion_pending: '5 · Discussion Pending',
    discussed: '6 · Discussed (complete)',
    expired: '7 · Expired (auto-closed)',
  };
  const currentStatusIdx = OVERRIDE_STATUS_ORDER.indexOf(review.status);

  const submitOverride = async () => {
    const reason = (override.reason || '').trim();
    if (reason.length < 5) { toast.error('Please provide a reason (min 5 characters)'); return; }
    const payload = { reason };
    if (override.manager_user_id && override.manager_user_id !== review.manager_user_id) payload.manager_user_id = override.manager_user_id;
    if (override.area_manager_user_id !== (review.area_manager_user_id || '')) payload.area_manager_user_id = override.area_manager_user_id || null;
    if (override.status && override.status !== review.status) payload.status = override.status;
    if (Object.keys(payload).length === 1) {
      toast.error('Change at least one field'); return;
    }
    setOverrideSaving(true);
    try {
      await fiveStarApi.adminOverride(id, payload);
      toast.success('Review overridden — all captured data preserved');
      setShowOverride(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Override failed');
    } finally {
      setOverrideSaving(false);
    }
  };

  // ── Pre-Synthesis Edit Permissions & Renderers ─────────────────────────
  // Manager OR Area Manager may edit the Manager Rating while the workflow
  // is at or before synthesis. Area Manager may edit their own AM Review
  // through synthesis as well. Status never advances on edit.
  const PRE_SYNTHESIS = ['manager_rating', 'area_manager_review', 'manager_synthesis'];
  const canEditMgrRating = (isManager || isAreaManager)
    && PRE_SYNTHESIS.includes(review.status)
    && !!review.manager_rating;
  const canEditAmReview = isAreaManager
    && ['area_manager_review', 'manager_synthesis'].includes(review.status)
    && !review.area_manager_opted_out
    && !!review.area_manager_review;

  const renderMgrRatingPanel = ({ title = "Manager's Rating", hideComments = false } = {}) => {
    if (editingMgrRating && canEditMgrRating) {
      return (
        <Card title={`Edit ${title}`} icon={PenSquare} testId="edit-mgr-rating-card">
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3 flex items-start gap-2">
            <PenSquare className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>Edits update the original Manager Rating. The workflow status does not change.</span>
          </div>
          <RatingForm
            categories={categories}
            onSave={handleEditManagerRating}
            submitLabel="Save changes"
            savingLabel="Saving…"
            commentsRequired
            requireOverall
            withStrengthsAndGrowth
            initialRatings={review.manager_rating?.ratings}
            initialComment={review.manager_rating?.overall_comment}
            initialStrengths={review.manager_rating?.strengths}
            initialGrowthAreas={review.manager_rating?.growth_areas}
          />
          <div className="mt-3 text-right">
            <button
              type="button"
              onClick={() => setEditingMgrRating(false)}
              data-testid="cancel-edit-mgr-rating-btn"
              className="text-xs font-semibold text-slate-500 hover:text-slate-800 px-3 py-1.5 rounded-md hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </Card>
      );
    }
    return (
      <div className="relative">
        <ReadOnlyRatings title={title} by={review.manager_name} when={review.manager_rating?.submitted_at}
          ratings={review.manager_rating?.ratings} comment={review.manager_rating?.overall_comment} color="cyan" hideComments={hideComments} />
        {canEditMgrRating && (
          <button
            type="button"
            onClick={() => setEditingMgrRating(true)}
            data-testid="edit-mgr-rating-btn"
            className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold bg-white border border-cyan-200 text-cyan-700 hover:bg-cyan-50 hover:border-cyan-300 transition shadow-sm"
            title="Edit the Manager Rating (allowed until synthesis is submitted)"
          >
            <PenSquare className="w-3 h-3" /> Edit
          </button>
        )}
      </div>
    );
  };

  const renderAmReviewPanel = ({ title = 'Area Manager Input' } = {}) => {
    if (editingAmReview && canEditAmReview) {
      return (
        <Card title={`Edit ${title}`} icon={MessageSquare} testId="edit-am-review-card">
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3 flex items-start gap-2">
            <PenSquare className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>Edits update your original Area Manager Review. The workflow status does not change.</span>
          </div>
          <RatingForm
            categories={categories}
            allowEmptyRatings
            onSave={handleEditAreaManagerReview}
            submitLabel="Save changes"
            savingLabel="Saving…"
            initialRatings={review.area_manager_review?.ratings}
            initialComment={review.area_manager_review?.overall_comment}
            initialStrengths={review.area_manager_review?.strengths}
            initialGrowthAreas={review.area_manager_review?.growth_areas}
          />
          <div className="mt-3 text-right">
            <button
              type="button"
              onClick={() => setEditingAmReview(false)}
              data-testid="cancel-edit-am-review-btn"
              className="text-xs font-semibold text-slate-500 hover:text-slate-800 px-3 py-1.5 rounded-md hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </Card>
      );
    }
    if (!review.area_manager_review) return null;
    return (
      <div className="relative">
        <ReadOnlyRatings title={title} by={review.area_manager_name} when={review.area_manager_review?.submitted_at}
          ratings={review.area_manager_review?.ratings} comment={review.area_manager_review?.overall_comment} color="magenta" />
        {canEditAmReview && (
          <button
            type="button"
            onClick={() => setEditingAmReview(true)}
            data-testid="edit-am-review-btn"
            className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold bg-white border border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50 hover:border-fuchsia-300 transition shadow-sm"
            title="Edit your Area Manager Review (allowed until synthesis is submitted)"
          >
            <PenSquare className="w-3 h-3" /> Edit
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="px-12 pt-8 pb-20 max-w-[1400px]" data-testid="five-star-review-page">
      <Toaster position="top-right" richColors />
      <Link to="/five-star" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 font-medium mb-3">
        <ArrowLeft className="w-3 h-3" /> Back to all reviews
      </Link>
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-[28px] font-extrabold tracking-tight text-slate-900">
            {review.cycle_name} — {review.employee_name}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Initiated {new Date(review.created_at).toLocaleDateString()} · Status: <strong className="text-fuchsia-600">{review.status.replace(/_/g, ' ')}</strong>
          </p>
        </div>
        {canReassignAm && (
          <button
            type="button"
            onClick={openReassign}
            data-testid="reassign-am-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-cyan-200 text-cyan-700 hover:bg-cyan-50 hover:border-cyan-300 transition"
            title="Reassign the Area Manager (available before synthesis)"
          >
            <UserCog className="w-3.5 h-3.5" /> {review.area_manager_user_id ? 'Reassign Area Manager' : 'Add Area Manager'}
          </button>
        )}
        {isSuperAdmin && (
          <button
            type="button"
            onClick={openOverride}
            data-testid="admin-override-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400 transition"
            title="Super-admin: change manager / area manager / rewind status"
          >
            <Shield className="w-3.5 h-3.5" /> Admin Override
          </button>
        )}
        {canDelete && (
          <button
            type="button"
            onClick={handleDeleteReview}
            data-testid="delete-review-detail-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-300 transition"
            title="Super-admin: permanently delete this review"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete review
          </button>
        )}
      </div>

      <Stepper
        status={review.status}
        onStepClick={(i) => setStageHistoryIdx(i)}
        canViewHistory={isManager || isAreaManager}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div>
          {/* Step 1: Manager Rating */}
          {review.status === 'manager_rating' && isManager && (
            <Card title="Step 1 — Your Manager Rating" icon={PenSquare} testId="step-manager-rating">
              <div className="text-xs text-slate-500 mb-3">
                Rate each category from 1 to 5. Per-category and overall comments are <strong>required</strong>.
              </div>
              <RatingForm
                categories={categories}
                onSave={handleManagerRate}
                submitLabel={review.area_manager_user_id && !review.area_manager_opted_out ? 'Submit & forward to Area Manager' : 'Submit & proceed to synthesis'}
                commentsRequired
                requireOverall
                withStrengthsAndGrowth
                initialRatings={review.drafts?.manager_rating?.data?.ratings}
                initialComment={review.drafts?.manager_rating?.data?.overall_comment}
                initialStrengths={review.drafts?.manager_rating?.data?.strengths}
                initialGrowthAreas={review.drafts?.manager_rating?.data?.growth_areas}
                autosave={(body) => fiveStarApi.saveDraft(id, 'manager_rating', body)}
              />
            </Card>
          )}
          {review.status === 'manager_rating' && !isManager && (
            <Card title="Waiting on Manager"><div className="text-sm text-slate-500">The manager ({review.manager_name}) is still rating.</div></Card>
          )}

          {/* Step 2: Area Manager Review */}
          {review.status === 'area_manager_review' && isAreaManager && (
            <>
              {renderMgrRatingPanel({ title: "Manager's Rating", hideComments: true })}
              <ManagerCommentsPanel
                reviewId={id}
                ratings={review.manager_rating?.ratings}
                overallComment={review.manager_rating?.overall_comment}
                by={review.manager_name}
                when={review.manager_rating?.submitted_at}
                pushbackNotes={review.am_pushback_notes}
                canEditPushback
                onPushbackChanged={reload}
              />
              <Card title="Step 2 — Area Manager Review" icon={MessageSquare} testId="step-area-manager">
                <div className="text-xs text-slate-500 mb-3">
                  You don't have to re-rate. Add comments or override ratings only where you want to push back or add context.
                </div>
                <RatingForm
                  categories={categories}
                  allowEmptyRatings
                  onSave={handleAreaManagerReview}
                  submitLabel="Submit Area Manager Review"
                  initialRatings={review.drafts?.area_manager_review?.data?.ratings}
                  initialComment={review.drafts?.area_manager_review?.data?.overall_comment}
                  autosave={(body) => fiveStarApi.saveDraft(id, 'area_manager_review', body)}
                />
                {/* Item #2 — Opt-out button */}
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                  <div className="text-slate-500 italic">
                    Not the right time to weigh in? You can opt out and the review will proceed without your input.
                  </div>
                  <button
                    type="button"
                    onClick={handleAreaManagerOptOut}
                    data-testid="am-opt-out-btn"
                    className="px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold"
                  >
                    Opt out of this review
                  </button>
                </div>
              </Card>
            </>
          )}
          {review.status === 'area_manager_review' && !isAreaManager && (
            <>
              {isManager && renderMgrRatingPanel({ title: "Manager's Rating" })}
              <Card title="Waiting on Area Manager"><div className="text-sm text-slate-500">The area manager ({review.area_manager_name}) is reviewing.</div></Card>
            </>
          )}

          {/* Step 3: Manager Synthesis */}
          {review.status === 'manager_synthesis' && isManager && (
            <>
              {renderMgrRatingPanel({ title: 'Your Initial Rating', hideComments: true })}
              <ManagerCommentsPanel
                reviewId={id}
                ratings={review.manager_rating?.ratings}
                overallComment={review.manager_rating?.overall_comment}
                by={review.manager_name}
                when={review.manager_rating?.submitted_at}
                pushbackNotes={review.am_pushback_notes}
                canEditPushback={false}
              />
              {renderAmReviewPanel({ title: 'Area Manager Input' })}
              <Card title="Step 3 — Synthesize Final Review" icon={FileText} testId="step-synthesis">
                <SynthesisForm categories={categories} review={review} onSave={handleSynthesis} />
              </Card>
            </>
          )}
          {review.status === 'manager_synthesis' && !isManager && isAreaManager && (
            <>
              <div className="rounded-xl border border-cyan-200 bg-cyan-50/40 p-4 mb-4 text-xs text-cyan-800 flex items-start gap-2" data-testid="am-synthesis-edit-banner">
                <Eye className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>{review.manager_name}</strong> is synthesizing the final review. You can still edit your Area Manager Review or the Manager Rating until the synthesis is submitted.
                </span>
              </div>
              {renderMgrRatingPanel({ title: "Manager's Rating" })}
              {renderAmReviewPanel({ title: 'Your Area Manager Review' })}
              <Card title="Waiting on Manager Synthesis"><div className="text-sm text-slate-500">{review.manager_name} is consolidating the inputs.</div></Card>
            </>
          )}
          {review.status === 'manager_synthesis' && !isManager && !isAreaManager && (
            <Card title="Waiting on Manager Synthesis"><div className="text-sm text-slate-500">{review.manager_name} is consolidating the inputs.</div></Card>
          )}

          {/* Step 4: Sign-off */}
          {review.status === 'signed_off' && isManager && (
            <>
              <ReadOnlyRatings title="Final Synthesis" by={review.manager_name} when={review.synthesis?.submitted_at}
                ratings={review.synthesis?.final_ratings} comment={review.synthesis?.summary} color="emerald" />
              <Card title="Step 4 — Final Sign-off" icon={CheckCircle2} testId="step-sign-off">
                <div className="text-sm text-slate-600 mb-4">Confirm the synthesis above and sign off. After sign-off, you'll discuss the review with the employee.</div>
                <button onClick={handleSignOff} data-testid="sign-off-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-emerald-500 to-emerald-700 text-white shadow hover:shadow-lg">
                  <CheckCircle2 className="w-4 h-4" /> Sign Off
                </button>
              </Card>
            </>
          )}

          {/* Step 5: Discussion */}
          {review.status === 'discussion_pending' && (
            <>
              <ReadOnlyRatings title="Final Synthesis" by={review.manager_name} when={review.synthesis?.submitted_at}
                ratings={review.synthesis?.final_ratings} comment={isEmployee && !isManager ? null : review.synthesis?.summary} color="emerald"
                hideComments={isEmployee && !isManager} />
              {isEmployee && !isManager && review.synthesis?.summary && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-5 mb-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-emerald-800 mb-2">Your Review Summary</div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{review.synthesis.summary}</div>
                </div>
              )}
              {(isManager || isEmployee) && (
                <Card title="Step 5 — Employee Discussion" icon={UsersIcon} testId="step-discussion">
                  <div className="text-sm text-slate-600 mb-5">
                    {isManager
                      ? 'Capture what was discussed, agreed-upon goals, and any follow-up. The employee will be able to review this after archiving.'
                      : `${review.manager_name} signed off your review. Capture your reflections and acknowledge after your discussion together.`}
                  </div>
                  <DiscussionForm onSave={handleDiscuss} isManager={isManager} isEmployee={isEmployee} review={review} />
                </Card>
              )}
            </>
          )}

          {/* Archived / Discussed — full Results view */}
          {review.status === 'discussed' && (
            <ResultsView
              review={review}
              categories={categories}
              trend={trend}
              discussion={review.discussion}
              viewerRole={isEmployee && !isManager && !isAreaManager ? 'employee' : 'manager'}
              onExportPdf={handleExportPdf}
            />
          )}
        </div>

        <SidebarInfo review={review} />
      </div>

      <StageHistoryModal
        open={stageHistoryIdx != null}
        stepIndex={stageHistoryIdx}
        review={review}
        categories={categories}
        onClose={() => setStageHistoryIdx(null)}
        canEditMgrRating={canEditMgrRating}
        canEditAmReview={canEditAmReview}
        onEditManagerRating={() => setEditingMgrRating(true)}
        onEditAreaManagerReview={() => setEditingAmReview(true)}
      />

      {showReassignAm && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowReassignAm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="reassign-am-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <UserCog className="w-5 h-5 text-cyan-600" />
                {review.area_manager_user_id ? 'Reassign Area Manager' : 'Add Area Manager'}
              </h3>
              <button onClick={() => setShowReassignAm(false)} className="p-1 rounded hover:bg-slate-100" type="button">
                <span className="text-slate-400 text-lg">×</span>
              </button>
            </div>

            {review.area_manager_user_id && (
              <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600">
                <div className="font-semibold text-slate-700 mb-0.5">Current Area Manager</div>
                <div>{review.area_manager_name} <span className="text-slate-400">· {review.area_manager_email}</span></div>
              </div>
            )}

            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              {review.area_manager_user_id ? 'Replacement' : 'Area Manager'} <span className="text-rose-500">*</span>
            </label>
            <EmployeeTypeahead
              value={reassignAmId}
              onChange={setReassignAmId}
              options={amCandidates}
              placeholder="Search area managers…"
              testId="reassign-am-typeahead"
            />
            <div className="mt-1 text-[11px] text-slate-400">
              Only review-initiators can be assigned. Allowed while the review is in {`Manager Rating`} or {`Area Manager Review`} — once synthesis starts, this option disappears.
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setShowReassignAm(false)} className="px-4 py-2 text-sm font-semibold rounded-lg border border-slate-200 hover:bg-slate-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={submitReassign}
                disabled={!reassignAmId || reassigning}
                data-testid="reassign-am-submit"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-cyan-600 text-white shadow hover:bg-cyan-700 disabled:opacity-50"
              >
                <UserCog className="w-4 h-4" /> {reassigning ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showOverride && (
        <div className="fixed inset-0 bg-slate-900/55 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !overrideSaving && setShowOverride(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-override-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-600" /> Admin Override
              </h3>
              <button onClick={() => !overrideSaving && setShowOverride(false)} className="p-1 rounded hover:bg-slate-100" type="button">
                <span className="text-slate-400 text-lg">×</span>
              </button>
            </div>

            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2">
              <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Super-admin surgical tool.</strong> Change any subset of manager, area manager, and status.
                Status can only be <strong>rewound</strong> (moved to an earlier step). All captured data at every stage is <strong>preserved</strong> — users simply resubmit to overwrite.
              </div>
            </div>

            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Manager (DOR)</label>
            <select
              value={override.manager_user_id}
              onChange={(e) => setOverride({ ...override, manager_user_id: e.target.value })}
              data-testid="override-manager-select"
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg mb-1 focus:border-amber-400 focus:outline-none"
            >
              <option value="">— Keep current ({review.manager_name}) —</option>
              {overrideCandidates.map(c => <option key={c.id} value={c.id}>{c.display_name} ({c.email})</option>)}
            </select>
            <div className="text-[11px] text-slate-400 mb-4">Current: <strong>{review.manager_name}</strong> · {review.manager_email}</div>

            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Area Manager</label>
            <select
              value={override.area_manager_user_id}
              onChange={(e) => setOverride({ ...override, area_manager_user_id: e.target.value })}
              data-testid="override-am-select"
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg mb-1 focus:border-amber-400 focus:outline-none"
            >
              <option value="">— Keep current —</option>
              {overrideCandidates.map(c => <option key={c.id} value={c.id}>{c.display_name} ({c.email})</option>)}
            </select>
            <div className="text-[11px] text-slate-400 mb-4">
              Current: {review.area_manager_name ? <><strong>{review.area_manager_name}</strong> · {review.area_manager_email}</> : <em>none</em>}
              <span className="block mt-0.5">💡 Picking the same person as the Manager marks the review as manager-self-attested and skips the Area Manager step.</span>
            </div>

            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Status (rewind only)</label>
            <select
              value={override.status}
              onChange={(e) => setOverride({ ...override, status: e.target.value })}
              data-testid="override-status-select"
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg mb-1 focus:border-amber-400 focus:outline-none"
            >
              {OVERRIDE_STATUS_ORDER.map((s, i) => (
                <option key={s} value={s} disabled={currentStatusIdx >= 0 && i > currentStatusIdx && s !== 'expired'}>
                  {OVERRIDE_STATUS_LABEL[s]}{currentStatusIdx >= 0 && i > currentStatusIdx && s !== 'expired' ? ' (forward — disabled)' : ''}
                  {s === review.status ? ' · current' : ''}
                </option>
              ))}
            </select>
            <div className="text-[11px] text-slate-400 mb-4">Rewinding preserves all captured data — the assigned user simply re-submits to overwrite.</div>

            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Reason <span className="text-rose-500">*</span>
            </label>
            <textarea
              value={override.reason}
              onChange={(e) => setOverride({ ...override, reason: e.target.value })}
              placeholder="Why is this override necessary? (min 5 chars — visible in the Activity Log)"
              data-testid="override-reason"
              rows={3}
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg focus:border-amber-400 focus:outline-none resize-y"
            />

            <div className="mt-5 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setShowOverride(false)} disabled={overrideSaving}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={submitOverride}
                disabled={overrideSaving || (override.reason || '').trim().length < 5}
                data-testid="override-submit"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-amber-600 text-white shadow hover:bg-amber-700 disabled:opacity-50"
              >
                <Shield className="w-4 h-4" /> {overrideSaving ? 'Saving…' : 'Apply Override'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FiveStarReviewPage;
